import { useCallback, useEffect, useRef, useState } from 'react'
import { popupService, type PopupData } from '../services/popupService'

interface UsePopupManagerOptions {
  tenantId: string | null
  route?: string
  enabled?: boolean
}

interface PopupWebSocketEventDetail {
  type?: string
  popupId?: string
  popup?: PopupData
  data?: {
    enabled?: boolean
    popupSystemEnabled?: boolean
    tenantId?: string
  }
  tenantId?: string
}

const POPUP_SYSTEM_STORAGE_PREFIX = 'popup-system-toggle-'

export const usePopupManagerOptimized = ({
  tenantId,
  route = '/',
  enabled = true,
}: UsePopupManagerOptions) => {
  const [currentPopup, setCurrentPopup] = useState<PopupData | null>(null)
  const [popupQueue, setPopupQueue] = useState<PopupData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [popupSystemEnabled, setPopupSystemEnabled] = useState<boolean | null>(null)
  const hasInitialized = useRef(false)

  const getStorageKey = useCallback(
    (popupTenantId: string) => `${POPUP_SYSTEM_STORAGE_PREFIX}${popupTenantId}`,
    []
  )

  const readSystemStatusFromStorage = useCallback(
    (popupTenantId: string) => {
      try {
        const stored = localStorage.getItem(getStorageKey(popupTenantId))
        return stored === null ? null : stored === 'true'
      } catch {
        return null
      }
    },
    [getStorageKey]
  )

  const writeSystemStatusToStorage = useCallback(
    (popupTenantId: string, status: boolean) => {
      try {
        localStorage.setItem(getStorageKey(popupTenantId), String(status))
      } catch {
        // ignore storage failures
      }
    },
    [getStorageKey]
  )

  const getHashPath = useCallback(() => {
    const rawHash = window.location.hash || '#/'
    const normalized = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
    const withLeadingSlash = normalized.startsWith('/') ? normalized : `/${normalized}`
    return withLeadingSlash.split('?')[0] || '/'
  }, [])

  const getEligiblePopupRoute = useCallback(() => {
    const currentRoute = getHashPath()
    if (currentRoute === '/' || currentRoute === '/dashboard') {
      return '/'
    }
    if (route !== '/') {
      return route
    }
    return null
  }, [getHashPath, route])

  const isPopupEligibleRoute = useCallback(() => getEligiblePopupRoute() !== null, [getEligiblePopupRoute])

  const shouldShowPopups = enabled && tenantId && isPopupEligibleRoute() && popupSystemEnabled === true

  const processPopupQueue = useCallback((popups: PopupData[]) => {
    if (!popups || popups.length === 0) {
      setPopupQueue([])
      setCurrentPopup(null)
      return
    }

    const availablePopups = popups.filter((popup) => !popupService.isPopupDismissed(popup._id, tenantId || popup.tenantId))
    if (availablePopups.length === 0) {
      setPopupQueue([])
      setCurrentPopup(null)
      return
    }

    const forceShowPopups = availablePopups.filter((popup) => popup.forceShow)
    const regularPopups = availablePopups.filter((popup) => !popup.forceShow)
    const sortedPopups = [...forceShowPopups, ...regularPopups].sort((a, b) => b.priority - a.priority)

    setPopupQueue(sortedPopups.slice(1))
    setCurrentPopup(sortedPopups[0] || null)
  }, [tenantId])

  const fetchInitialPopups = useCallback(
    async (forceEnabled?: boolean) => {
      const systemEnabled = forceEnabled !== undefined ? forceEnabled : popupSystemEnabled === true
      const popupRoute = getEligiblePopupRoute()

      if (!enabled || !tenantId || !systemEnabled || !popupRoute) {
        setCurrentPopup(null)
        setPopupQueue([])
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        const popups = await popupService.getActivePopups(tenantId, popupRoute)
        processPopupQueue(popups)
      } catch (fetchError) {
        setError(fetchError instanceof Error ? fetchError.message : 'Failed to fetch popups')
      } finally {
        setIsLoading(false)
      }
    },
    [enabled, getEligiblePopupRoute, popupSystemEnabled, processPopupQueue, tenantId]
  )

  const dismissPopup = useCallback(
    async (popupId: string, dontShowAgain: boolean = false) => {
      if (!currentPopup || !tenantId) return

      const remainingQueue = popupQueue.filter((popup) => popup._id !== popupId)

      if (dontShowAgain) {
        const reshowDailyAt =
          currentPopup?._id === popupId ? currentPopup.reshowDailyAt : undefined
        popupService.storeDismissedPopup(popupId, tenantId, reshowDailyAt)
        await popupService.dismissPopup(popupId, tenantId, 'manual')
      } else {
        void popupService.dismissPopup(popupId, tenantId, 'manual')
      }

      if (remainingQueue.length > 0) {
        const [nextPopup, ...nextQueue] = remainingQueue
        setCurrentPopup(nextPopup)
        setPopupQueue(nextQueue)
      } else {
        setCurrentPopup(null)
        setPopupQueue([])
      }
    },
    [currentPopup, popupQueue, tenantId]
  )

  const forceShowPopup = useCallback((popup: PopupData) => {
    setCurrentPopup({
      ...popup,
      isForceShow: true,
      allowDismiss: popup.allowDismiss !== false,
    })
  }, [])

  const checkPopupSystemStatus = useCallback(async () => {
    if (!tenantId) return false

    if (!isPopupEligibleRoute()) {
      setPopupSystemEnabled(false)
      return false
    }

    const cachedStatus = readSystemStatusFromStorage(tenantId)
    if (cachedStatus !== null) {
      setPopupSystemEnabled(cachedStatus)
    }

    try {
      const response = await fetch(
        `${popupService.getBaseUrl()}/api/v1/configuration-service/popups/${tenantId}/system-status`
      )
      if (!response.ok) {
        if (cachedStatus !== null) return cachedStatus
        throw new Error(`HTTP ${response.status}`)
      }

      const data = (await response.json()) as {
        success?: boolean
        data?: { enabled?: boolean; popupSystemEnabled?: boolean }
      }

      const resolvedEnabledStatus =
        typeof data?.data?.popupSystemEnabled === 'boolean'
          ? data.data.popupSystemEnabled
          : typeof data?.data?.enabled === 'boolean'
            ? data.data.enabled
            : null

      const enabledStatus = data?.success && resolvedEnabledStatus !== null
        ? resolvedEnabledStatus
        : cachedStatus ?? true

      setPopupSystemEnabled(enabledStatus)
      writeSystemStatusToStorage(tenantId, enabledStatus)
      return enabledStatus
    } catch (statusError) {
      console.warn('Failed to check popup system status:', statusError)
      if (cachedStatus !== null) {
        setPopupSystemEnabled(cachedStatus)
        return cachedStatus
      }
      setPopupSystemEnabled(true)
      return true
    }
  }, [isPopupEligibleRoute, readSystemStatusFromStorage, tenantId, writeSystemStatusToStorage])

  useEffect(() => {
    if (!enabled || !tenantId) return

    if (!isPopupEligibleRoute()) {
      setPopupSystemEnabled(false)
      hasInitialized.current = false
      setCurrentPopup(null)
      setPopupQueue([])
      return
    }

    let mounted = true

    const initialize = async () => {
      if (hasInitialized.current) return
      hasInitialized.current = true

      const systemEnabled = await checkPopupSystemStatus()
      if (!mounted) return

      if (systemEnabled && isPopupEligibleRoute()) {
        window.setTimeout(() => {
          if (mounted) {
            fetchInitialPopups(true)
          }
        }, 250)
      }
    }

    void initialize()

    return () => {
      mounted = false
    }
  }, [checkPopupSystemStatus, enabled, fetchInitialPopups, isPopupEligibleRoute, tenantId])

  useEffect(() => {
    if (!isPopupEligibleRoute()) {
      setCurrentPopup(null)
      setPopupQueue([])
    } else if (popupSystemEnabled === true && hasInitialized.current) {
      void fetchInitialPopups(true)
    }
  }, [fetchInitialPopups, isPopupEligibleRoute, popupSystemEnabled])

  useEffect(() => {
    const syncRoute = () => {
      if (!isPopupEligibleRoute()) {
        setCurrentPopup(null)
        setPopupQueue([])
        return
      }

      if (tenantId && popupSystemEnabled === true && hasInitialized.current) {
        void fetchInitialPopups(true)
      }
    }

    window.addEventListener('hashchange', syncRoute)
    window.addEventListener('popstate', syncRoute)
    return () => {
      window.removeEventListener('hashchange', syncRoute)
      window.removeEventListener('popstate', syncRoute)
    }
  }, [fetchInitialPopups, isPopupEligibleRoute, popupSystemEnabled, tenantId])

  useEffect(() => {
    const handlePopupEvent = (event: Event) => {
      const customEvent = event as CustomEvent<PopupWebSocketEventDetail>
      const detail = customEvent.detail || {}
      const eventTenantId = detail.tenantId || detail.data?.tenantId

      if (tenantId && eventTenantId && eventTenantId !== tenantId) {
        return
      }

      switch (detail.type) {
        case 'popup_show':
          if (detail.popup && shouldShowPopups && !popupService.isPopupDismissed(detail.popup._id, tenantId || detail.popup.tenantId)) {
            forceShowPopup(detail.popup)
          }
          break
        case 'popup_system_toggle':
        case 'admin_popup_system_toggle': {
          const enabledStatus =
            typeof detail.data?.enabled === 'boolean'
              ? detail.data.enabled
              : detail.data?.popupSystemEnabled === true
          setPopupSystemEnabled(enabledStatus)
          if (tenantId) {
            writeSystemStatusToStorage(tenantId, enabledStatus)
          }
          if (!enabledStatus) {
            setCurrentPopup(null)
            setPopupQueue([])
          } else if (tenantId && isPopupEligibleRoute()) {
            void fetchInitialPopups(true)
          }
          break
        }
        case 'popup_clear_sessions':
          if (tenantId) {
            popupService.clearPopupDismissals(tenantId)
            void fetchInitialPopups(true)
          }
          break
        case 'popup_refresh':
          if (shouldShowPopups) {
            void fetchInitialPopups(true)
          }
          break
        default:
          break
      }
    }

    window.addEventListener('popup_websocket_event', handlePopupEvent as EventListener)
    return () => {
      window.removeEventListener('popup_websocket_event', handlePopupEvent as EventListener)
    }
  }, [
    fetchInitialPopups,
    forceShowPopup,
    isPopupEligibleRoute,
    shouldShowPopups,
    tenantId,
    writeSystemStatusToStorage,
  ])

  return {
    currentPopup,
    popupQueue,
    isLoading,
    error,
    dismissPopup,
    forceShowPopup,
    refreshPopups: fetchInitialPopups,
  }
}

export default usePopupManagerOptimized

